import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import s from './UnitSettings.module.scss';
import { FormattedMessage, injectIntl } from 'react-intl';
import Button from 'react-bootstrap/Button';
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';
import Banner from '../Banner/Banner';
import { useParams } from 'react-router-dom';
import ContentTemplate from '../ContentTemplate/ContentTemplate';
import { getUserProfileWidget, getCurrentTimezone, getDevicesUnitConfigSettings, getDeviceIsLoading } from '../../selectors/index';
import { sendCommand, getUnitSettingsConfigThunk, uploadUnitConfigFileThunk, cleanUpConfigFileThunk } from '../../actions/devices';

import { IsWidgetAccessible } from '../../utils/widgetManager';
import { COMMAND_MODE_SYSTEM, COMMAND_MODE_MANUAL, COMMAND_MODE_AUTO } from '../../constants';
import { getBannerStatus, getDeviceErrorStatus, getDeviceDisplayMessage } from '../../selectors';
import { JsonEditor, githubLightTheme } from 'json-edit-react';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import moment from 'moment-timezone';


// const jsonEditorTheme = {
//   displayName: 'Default',
//   fragments: { edit: 'rgb(42, 161, 152)' },
//   styles: {
//     container: {
//       fontFamily: '"Barlow", Helvetica, Arial, sans-serif',
//       maxWidth: '100%'
//     }
//   }
// };

const UnitSettings = ({ intl }) => {
  const dispatch = useDispatch();
  const commandIdEnableLog = 100;
  const commandIdRebootFirmware = 120;
  const usbReset = 102;
  const firmwareUsbReset = 111;
  const selfService = 501;
  const emergencyStop = 101;
  const greengrassRestart = 103;
  const hardwareConfigFetch = 130;
  const commsConfigFetch = 131;

  const userProfileWidgets = useSelector(state => getUserProfileWidget(state));
  const deviceConfigSettings = useSelector(state => getDevicesUnitConfigSettings(state));
  const isLoading = useSelector(state => getDeviceIsLoading(state));
  const currentTimezone = useSelector(state => getCurrentTimezone(state));

  const enableLogAllowed = IsWidgetAccessible(userProfileWidgets, 'IOTUNTISETTINGCOMMAND_100');
  const emergencyStopAllowed = IsWidgetAccessible(userProfileWidgets, 'IOTUNTISETTINGCOMMAND_101');
  const firmwareRebootAllowed = IsWidgetAccessible(userProfileWidgets, 'IOTUNTISETTINGCOMMAND_120');
  const selfServiceAllowed = IsWidgetAccessible(userProfileWidgets, 'IOTUNTISETTINGCOMMAND_501');
  const usbResetAllowed = IsWidgetAccessible(userProfileWidgets, 'IOTUNTISETTINGCOMMAND_102');
  const greengrassRestartAllowed = IsWidgetAccessible(userProfileWidgets, 'IOTUNTISETTINGCOMMAND_103');
  const firmwareUsbResetAllowed = IsWidgetAccessible(userProfileWidgets, 'IOTUNTISETTINGCOMMAND_111');

  const enableHwConfigFetch = IsWidgetAccessible(userProfileWidgets, 'IOTUNTISETTINGCOMMAND_130');
  const enableCommsConfigFetch = IsWidgetAccessible(userProfileWidgets, 'IOTUNTISETTINGCOMMAND_131');

  const enableHwConfigUpdate = IsWidgetAccessible(userProfileWidgets, 'IOTUNTISETTINGHARDWARECONFIGUPDATE');
  const enableCommsConfigUpdate = IsWidgetAccessible(userProfileWidgets, 'IOTUNTISETTINGCOMMSCONFIGUPDATE');

  let fetchHwConfigWidget = 'IOTUNTISETTINGCOMMAND_130';
  if (enableHwConfigUpdate) {
    fetchHwConfigWidget = 'IOTUNTISETTINGHARDWARECONFIGUPDATE';
  }
  let fetchCommsConfigWidget = 'IOTUNTISETTINGCOMMAND_131';
  if (enableCommsConfigUpdate) {
    fetchCommsConfigWidget = 'IOTUNTISETTINGCOMMSCONFIGUPDATE';
  }

  let isCommsEditButtonVisible = enableCommsConfigUpdate && deviceConfigSettings && deviceConfigSettings.CommsConfigData && deviceConfigSettings.CommsConfigData.content ? true : false;
  let isHwEditButtonVisible = enableHwConfigUpdate && deviceConfigSettings && deviceConfigSettings.HwConfigData && deviceConfigSettings.HwConfigData.content ? true : false;

  const dialogTitleDefault = intl.formatMessage({
    id: 'command.areyousureyouwanttoactionthecommand',
    defaultMessage: 'Are you sure you want to action the command?'
  });

  const dialogTitleGetLatest = intl.formatMessage({
    id: 'command.areyousureyouwanttoretrivetheLatestConfig',
    defaultMessage: 'Are you sure you want to retrieve the latest configuration?'
  });

  const dialogTitleUpload = intl.formatMessage({
    id: 'unitSettings.areyousureyouwanttoUploadConfigFile',
    defaultMessage: 'Are you sure you want to upload the file to the unit?'
  });


  const errorStatus = useSelector(state => getDeviceErrorStatus(state));
  const displayMessage = useSelector(state => getDeviceDisplayMessage(state));
  const bannerStatus = useSelector(state => getBannerStatus(state));

  const [dialogTitle, setDialogTitle] = useState('');
  const [active, setActive] = useState(false);
  const [commandId, setCommandId] = useState(0);
  const [commandType, setCommandType] = useState(COMMAND_MODE_SYSTEM);
  const [commandValue, setCommandValue] = useState('');
  const { unitSerialNumber } = useParams();
  const [uploadActive, setUploadActive] = useState(false);
  const [uploadConfigFileName, setUploadConfigFileName] = useState('');


  const [editedHwConfig, setEditedHwConfig] = useState(null);
  const [editedCommsConfig, setEditedCommsConfig] = useState(null);


  useEffect(() => {
    return () => {
      dispatch(cleanUpConfigFileThunk());
    };
  }, []);


  //Confirm Dialog Handler for Upload
  const handleClose = () => {
    setActive(false);
    setUploadActive(false);
  }

  const handleCommand = (commandId, commandValue, dialogTitle = null) => {
    setDialogTitle(dialogTitle || dialogTitleDefault);
    setActive(true);
    setCommandId(commandId);
    if (commandId == selfService) {
      setCommandType(COMMAND_MODE_MANUAL);
    } else {
      setCommandType(COMMAND_MODE_AUTO);
    }
    setCommandValue(commandValue);
  };

  const handleConfirm = () => {
    dispatch(sendCommand(commandId, 'UNIT_SETTINGS', true, commandType, commandValue));
    handleClose();
  };

  const getConfigData = (wCode) => {
    dispatch(getUnitSettingsConfigThunk(unitSerialNumber, wCode));
  };

  const handleFileUploadConfirm = (configFileName) => {
    setUploadConfigFileName(configFileName);
    setUploadActive(true);
  }

  const handleFileUpload = () => {
    let configFile = uploadConfigFileName == 'HwConfig' ? editedHwConfig : editedCommsConfig;
    if (!configFile) {
      configFile = uploadConfigFileName == 'HwConfig' ? deviceConfigSettings && deviceConfigSettings.HwConfigData && deviceConfigSettings.HwConfigData.content || {} 
      : deviceConfigSettings && deviceConfigSettings.CommsConfigData && deviceConfigSettings.CommsConfigData.content || {};
    }
    let configFileUploadWidget = uploadConfigFileName == 'HwConfig' ? 'IOTUNTISETTINGHARDWARECONFIGUPDATE' : 'IOTUNTISETTINGCOMMSCONFIGUPDATE';
    dispatch(uploadUnitConfigFileThunk(unitSerialNumber, configFileUploadWidget, configFile));
    handleClose();
  };



  let messageId = displayMessage || '';
  let messageText = messageId != '' ? intl.formatMessage({ id: messageId, defaultMessage: messageId }) : '';
  messageText = bannerStatus ? messageText : '';

  return (
    <ContentTemplate
      selectedPage="unitsettings"
      userProfileWidgets={userProfileWidgets}
      widgetCode={'UNIT_SETTINGS'}
      tagoutRequired={false}
    >
      <div className={s.unitSettings}>
        <ConfirmDialog title={dialogTitle} onConfirm={handleConfirm} onClose={handleClose} showDialog={active} />
        <ConfirmDialog title={dialogTitleUpload} onConfirm={handleFileUpload} onClose={handleClose} showDialog={uploadActive} />
        <Banner failureText={messageText} showBanner={bannerStatus} status={errorStatus} successText={messageText} />
        {isLoading && <LoadingSpinner />}

        {/* Header Section */}
        <div className={s.pageHeader}>
          <h1 className={s.pageTitle}>
            <FormattedMessage id="unitSettings.title" defaultMessage="Unit Settings" />
          </h1>
          <p className={s.pageSubtitle}>
            <FormattedMessage id="unitSettings.subtitle" defaultMessage="Manage system commands and configuration settings" />
          </p>
        </div>

        {/* System Commands Section */}
        <div className={s.sectionCard}>
          <div className={s.sectionHeader}>
            <svg className={s.sectionIcon} width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M10 3.5C10 2.67157 10.6716 2 11.5 2C12.3284 2 13 2.67157 13 3.5V5H14C15.1046 5 16 5.89543 16 7V8H18C18.5523 8 19 8.44772 19 9C19 9.55228 18.5523 10 18 10H16V11H18C18.5523 11 19 11.4477 19 12C19 12.5523 18.5523 13 18 13H16V14C16 15.1046 15.1046 16 14 16H13V17.5C13 18.3284 12.3284 19 11.5 19C10.6716 19 10 18.3284 10 17.5V16H9V17.5C9 18.3284 8.32843 19 7.5 19C6.67157 19 6 18.3284 6 17.5V16H5C3.89543 16 3 15.1046 3 14V13H1C0.447715 13 0 12.5523 0 12C0 11.4477 0.447715 11 1 11H3V10H1C0.447715 10 0 9.55228 0 9C0 8.44772 0.447715 8 1 8H3V7C3 5.89543 3.89543 5 5 5H6V3.5C6 2.67157 6.67157 2 7.5 2C8.32843 2 9 2.67157 9 3.5V5H10V3.5Z" fill="currentColor" />
            </svg>
            <h2 className={s.sectionTitle}>
              <FormattedMessage id="unitSettings.systemCommands" defaultMessage="System Commands" />
            </h2>
          </div>

          <div className={s.commandsList}>
            {enableLogAllowed && (
              <div className={s.commandItem}>
                <div className={s.commandInfo}>
                  <svg className={s.commandIcon} width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M4 7C4 5.89543 4.89543 5 6 5H18C19.1046 5 20 5.89543 20 7V17C20 18.1046 19.1046 19 18 19H6C4.89543 19 4 18.1046 4 17V7Z" stroke="#2f92d0" strokeWidth="2" />
                    <path d="M7 9H17M7 12H17M7 15H13" stroke="#2f92d0" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                  <span className={s.commandLabel}>
                    <FormattedMessage id="unitSettings.enableSerialLogs" defaultMessage="Serial Logs" />
                  </span>
                </div>
                <div className={s.commandActions}>
                  <Button
                    variant="primary"
                    className={s.btnEnable}
                    onClick={() => handleCommand(commandIdEnableLog, 'true')}
                  >
                    <FormattedMessage id="unitSettings.enable" defaultMessage="Enable" />
                  </Button>
                  <Button
                    variant="secondary"
                    className={s.btnDisable}
                    onClick={() => handleCommand(commandIdEnableLog, 'false')}
                  >
                    <FormattedMessage id="unitSettings.disable" defaultMessage="Disable" />
                  </Button>
                </div>
              </div>
            )}

            {firmwareRebootAllowed && (
              <div className={s.commandItem}>
                <div className={s.commandInfo}>
                  <svg className={s.commandIcon} width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M4 12C4 7.58172 7.58172 4 12 4V2L15 5L12 8V6C8.68629 6 6 8.68629 6 12C6 15.3137 8.68629 18 12 18C15.3137 18 18 15.3137 18 12H20C20 16.4183 16.4183 20 12 20C7.58172 20 4 16.4183 4 12Z" fill="#e24545" />
                  </svg>
                  <span className={s.commandLabel}>
                    <FormattedMessage id="unitSettings.rebootFirmware" defaultMessage="Reboot Firmware" />
                  </span>
                </div>
                <div className={s.commandActions}>
                  <Button
                    variant="danger"
                    className={s.btnDanger}
                    onClick={() => handleCommand(commandIdRebootFirmware, 'Reboot')}
                  >
                    <FormattedMessage id="unitSettings.reboot" defaultMessage="Reboot" />
                  </Button>
                </div>
              </div>
            )}

            {firmwareUsbResetAllowed && (
              <div className={s.commandItem}>
                <div className={s.commandInfo}>
                  <svg className={s.commandIcon} width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M12 2L8 6H11V12H13V6H16L12 2Z" fill="#e24545" />
                    <path d="M5 18V9H3V18C3 19.66 4.34 21 6 21H18C19.66 21 21 19.66 21 18V9H19V18C19 18.55 18.55 19 18 19H6C5.45 19 5 18.55 5 18Z" fill="#e24545" />
                  </svg>
                  <span className={s.commandLabel}>
                    <FormattedMessage id="unitSettings.firmwareUsbReset" defaultMessage="Firmware USB Reset" />
                  </span>
                </div>
                <div className={s.commandActions}>
                  <Button
                    variant="danger"
                    className={s.btnDanger}
                    onClick={() => handleCommand(firmwareUsbReset, 'Firmware Usb Reset')}
                  >
                    <FormattedMessage id="unitSettings.reset" defaultMessage="Reset" />
                  </Button>
                </div>
              </div>
            )}

            {usbResetAllowed && (
              <div className={s.commandItem}>
                <div className={s.commandInfo}>
                  <svg className={s.commandIcon} width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M12 2L8 6H11V12H13V6H16L12 2Z" fill="#e24545" />
                    <path d="M5 18V9H3V18C3 19.66 4.34 21 6 21H18C19.66 21 21 19.66 21 18V9H19V18C19 18.55 18.55 19 18 19H6C5.45 19 5 18.55 5 18Z" fill="#e24545" />
                  </svg>
                  <span className={s.commandLabel}>
                    <FormattedMessage id="unitSettings.usbReset" defaultMessage="USB Reset" />
                  </span>
                </div>
                <div className={s.commandActions}>
                  <Button
                    variant="danger"
                    className={s.btnDanger}
                    onClick={() => handleCommand(usbReset, 'Usb Reset')}
                  >
                    <FormattedMessage id="unitSettings.reset" defaultMessage="Reset" />
                  </Button>
                </div>
              </div>
            )}

            {emergencyStopAllowed && (
              <div className={`${s.commandItem} ${s.emergencyItem}`}>
                <div className={s.commandInfo}>
                  <svg className={s.commandIcon} width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="9" stroke="#e24545" strokeWidth="2" />
                    <path d="M12 8V12M12 16H12.01" stroke="#e24545" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                  <span className={s.commandLabel}>
                    <FormattedMessage id="unitSettings.emergencyStop" defaultMessage="Emergency Unit Stop" />
                  </span>
                </div>
                <div className={s.commandActions}>
                  <Button
                    variant="danger"
                    className={s.btnEmergency}
                    onClick={() => handleCommand(emergencyStop, 'Emergency Stop')}
                  >
                    <FormattedMessage id="unitSettings.emergencyStop" defaultMessage="Emergency Stop" />
                  </Button>
                </div>
              </div>
            )}

            {greengrassRestartAllowed && (
              <div className={s.commandItem}>
                <div className={s.commandInfo}>
                  <svg className={s.commandIcon} width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M12 4V2L9 5L12 8V6C15.31 6 18 8.69 18 12C18 13.01 17.75 13.97 17.3 14.8L18.76 16.26C19.54 15.03 20 13.57 20 12C20 7.58 16.42 4 12 4ZM12 18C8.69 18 6 15.31 6 12C6 10.99 6.25 10.03 6.7 9.2L5.24 7.74C4.46 8.97 4 10.43 4 12C4 16.42 7.58 20 12 20V22L15 19L12 16V18Z" fill="#e24545" />
                  </svg>
                  <span className={s.commandLabel}>
                    <FormattedMessage id="unitSettings.greengrassRestart" defaultMessage="Greengrass Restart" />
                  </span>
                </div>
                <div className={s.commandActions}>
                  <Button
                    variant="danger"
                    className={s.btnDanger}
                    onClick={() => handleCommand(greengrassRestart, 'Greengrass Restart')}
                  >
                    <FormattedMessage id="unitSettings.restart" defaultMessage="Restart" />
                  </Button>
                </div>
              </div>
            )}

            {selfServiceAllowed && (
              <div className={s.commandItem}>
                <div className={s.commandInfo}>
                  <svg className={s.commandIcon} width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="#e24545" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M2 17L12 22L22 17M2 12L12 17L22 12" stroke="#e24545" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span className={s.commandLabel}>
                    <FormattedMessage id="unitSettings.maintenanceSelfService" defaultMessage="Maintenance (SelfService)" />
                  </span>
                </div>
                <div className={s.commandActions}>
                  <Button
                    variant="danger"
                    className={s.btnDanger}
                    onClick={() => handleCommand(selfService, 'SelfService State')}
                  >
                    <FormattedMessage id="unitSettings.maintenance" defaultMessage="Maintenance" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Hardware Config Section */}
        {(enableHwConfigFetch || enableHwConfigUpdate) && (
          <div className={s.sectionCard}>
            <div className={s.sectionHeader}>
              <svg className={s.sectionIcon} width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M3 5C3 3.89543 3.89543 3 5 3H15C16.1046 3 17 3.89543 17 5V15C17 16.1046 16.1046 17 15 17H5C3.89543 17 3 16.1046 3 15V5Z" stroke="currentColor" strokeWidth="2" />
                <path d="M7 7H13M7 10H13M7 13H10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
              <h2 className={s.sectionTitle}>
                <FormattedMessage id="unitSettings.hardwareConfigSettings" defaultMessage="Hardware Config Settings" />
              </h2>
            </div>

            <div className={s.configActions}>
              {enableHwConfigFetch && <Button
                variant="primary"
                className={s.btnPrimary}
                onClick={() => handleCommand(hardwareConfigFetch, 'Retrieve Hardware Config', dialogTitleGetLatest)}
              >
                <FormattedMessage id="unitSettings.getLatest" defaultMessage="Get Latest" />
              </Button>}
              <Button
                variant="primary"
                className={s.btnPrimary}
                onClick={() => getConfigData(fetchHwConfigWidget)}
              >
                {enableHwConfigUpdate && deviceConfigSettings && deviceConfigSettings.HwConfigData && deviceConfigSettings.HwConfigData.content ?
                  <FormattedMessage id="unitSettings.editConfig" defaultMessage="Edit Config" /> :
                  <FormattedMessage id="unitSettings.viewConfig" defaultMessage="View Config" />}
              </Button>
              {enableHwConfigUpdate && <Button
                variant="danger"
                className={s.btnDanger}
                disabled={!isHwEditButtonVisible}
                onClick={() => handleFileUploadConfirm('HwConfig')}
              >
                <FormattedMessage id="unitSettings.uploadFile" defaultMessage="Upload File" />
              </Button>}
            </div>

            {deviceConfigSettings && deviceConfigSettings.HwConfigData && (
              <div className={s.configContent}>
                <div className={s.configMeta}>
                  <span className={s.metaLabel}>
                    <FormattedMessage id="unitSettings.lastRetrieved" defaultMessage="Last Retrieved:" />
                  </span>
                  <span className={s.metaValue}>
                    {deviceConfigSettings.HwConfigData.lastModified
                      ? moment(deviceConfigSettings.HwConfigData.lastModified)
                        .tz(currentTimezone)
                        .format('DD-MMM-YYYY hh:mm:ss A')
                      : '-'}
                  </span>
                </div>
                <div className={s.jsonEditorWrapper}>
                  <JsonEditor
                    className={s.jsonEditor}
                    theme={githubLightTheme}
                    viewOnly={!enableHwConfigUpdate}
                    data={deviceConfigSettings.HwConfigData.content || {}}
                    collapse={false}
                    rootName="Config"
                    onUpdate={({ newData }) => setEditedHwConfig(newData)}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Communication Config Section */}
        {(enableCommsConfigFetch || enableCommsConfigUpdate) && (
          <div className={s.sectionCard}>
            <div className={s.sectionHeader}>
              <svg className={s.sectionIcon} width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M10 3C10 3 6 4 6 7V11C6 13 7 14 10 15C13 14 14 13 14 11V7C14 4 10 3 10 3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <circle cx="10" cy="10" r="2" fill="currentColor" />
              </svg>
              <h2 className={s.sectionTitle}>
                <FormattedMessage id="unitSettings.commsConfigSettings" defaultMessage="Communication Config Settings" />
              </h2>
            </div>

            <div className={s.configActions}>
              {enableCommsConfigFetch &&
                <Button
                  variant="primary"
                  className={s.btnPrimary}
                  onClick={() => handleCommand(commsConfigFetch, 'Retrieve Comms Config', dialogTitleGetLatest)}
                >
                  <FormattedMessage id="unitSettings.getLatest" defaultMessage="Get Latest" />
                </Button>
              }
              <Button
                variant="primary"
                className={s.btnPrimary}
                onClick={() => getConfigData(fetchCommsConfigWidget)}
              >
                {isCommsEditButtonVisible ?
                  <FormattedMessage id="unitSettings.editConfig" defaultMessage="Edit Config" /> :
                  <FormattedMessage id="unitSettings.viewConfig" defaultMessage="View Config" />
                }
              </Button>
              {enableCommsConfigUpdate && <Button
                variant="danger"
                className={s.btnDanger}
                disabled={!isCommsEditButtonVisible}

                onClick={() => handleFileUploadConfirm('CommsConfig')}
              >
                <FormattedMessage id="unitSettings.uploadFile" defaultMessage="Upload File" />
              </Button>}
            </div>

            {deviceConfigSettings && deviceConfigSettings.CommsConfigData && (
              <div className={s.configContent}>
                <div className={s.configMeta}>
                  <span className={s.metaLabel}>
                    <FormattedMessage id="unitSettings.lastRetrieved" defaultMessage="Last Retrieved:" />
                  </span>
                  <span className={s.metaValue}>
                    {deviceConfigSettings.CommsConfigData.lastModified
                      ? moment(deviceConfigSettings.CommsConfigData.lastModified)
                        .tz(currentTimezone)
                        .format('DD-MMM-YYYY hh:mm:ss A')
                      : '-'}
                  </span>
                </div>
                <div className={s.jsonEditorWrapper}>
                  <JsonEditor
                    className={s.jsonEditor}
                    theme={githubLightTheme}
                    viewOnly={!enableCommsConfigUpdate}
                    data={deviceConfigSettings.CommsConfigData.content || {}}
                    collapse={false}
                    rootName="Config"
                    onUpdate={({ newData }) => setEditedCommsConfig(newData)}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </ContentTemplate>
  );
};

export default injectIntl(UnitSettings);